import React, { ReactNode } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import MuiCheckbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiRadio from '@mui/material/Radio';
import MuiSwitch from '@mui/material/Switch';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CalendarBlank from '@mds/icons/CalendarBlank';

export interface SelectOption {
    label?: string;
    secondaryLabel?: string;
    title?: string;
    value: any;
    disabled?: boolean;
    indentation?: number;
    [key: string]: any;
}

export type Option = SelectOption;

export interface CommonTextFieldProps {
    id?: string;
    label?: ReactNode;
    value?: any;
    name?: string;
    fullWidth?: boolean;
    error?: boolean;
    errorText?: ReactNode;
    helperText?: ReactNode;
    disabled?: boolean;
    type?: string;
    autoComplete?: string;
    placeholder?: string;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onKeyPress?: React.KeyboardEventHandler<any>;
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const optionLabel = (option: SelectOption) =>
    option.label ?? option.title ?? String(option.value ?? '');
const optionKey = (value: any) => String(value ?? '');
const isOptionLike = (value: any): value is SelectOption =>
    !!value && typeof value === 'object' && 'value' in value;
const optionMatchesValue = (option: SelectOption, value: any) =>
    optionKey(option.value) ===
    optionKey(isOptionLike(value) ? value.value : value);
const resolveOption = (value: any, options: SelectOption[]) => {
    if (value == null || value === '') return null;
    return (
        options.find((option) => optionMatchesValue(option, value)) ??
        (isOptionLike(value) ? value : null)
    );
};

const resolveOptions = (value: any, options: SelectOption[]) =>
    Array.isArray(value)
        ? (value
              .map((item) => resolveOption(item, options))
              .filter(Boolean) as SelectOption[])
        : [];

export const TextField = ({
    errorText,
    helperText,
    ...props
}: CommonTextFieldProps & Record<string, any>) => (
    <MuiTextField
        size="medium"
        helperText={errorText ?? helperText}
        {...props}
    />
);

export const TextArea = ({
    errorText,
    helperText,
    progressBarIndicator,
    progressBarHelperText,
    progressBarLimit,
    rowsMax,
    rowsMin,
    showProgressBar,
    ...props
}: CommonTextFieldProps & {
    progressBarHelperText?: ReactNode;
    progressBarIndicator?: number;
    progressBarLimit?: number;
    rowsMax?: number;
    rowsMin?: number;
    showProgressBar?: boolean;
} & Record<string, any>) => {
    const resolvedHelperText =
        errorText ||
        helperText ||
        (showProgressBar ? progressBarHelperText : undefined);

    return (
        <MuiTextField
            size="medium"
            multiline
            minRows={rowsMin ?? 4}
            maxRows={rowsMax}
            helperText={resolvedHelperText}
            {...props}
        />
    );
};

export const Select = ({
    options = [],
    value,
    onChange,
    errorText,
    helperText,
    label,
    multi,
    multiple,
    className,
    loadOptions,
    clearable = true,
    searchable = true,
    placeholder,
    ariaLabel,
    hiddenLabel = false,
    size = 'small',
    fullWidth = true,
    error,
    disabled,
    hideSelectedOptions,
    onBlur,
    id,
    name,
    required,
    menuPlacement,
    ...props
}: {
    options?: SelectOption[];
    value?: any;
    onChange?: (...args: any[]) => void;
    errorText?: ReactNode;
    helperText?: ReactNode;
    label?: ReactNode;
    multi?: boolean;
    multiple?: boolean;
    className?: string;
    loadOptions?: (
        inputValue: string,
        callback: (options: SelectOption[]) => void
    ) => Promise<SelectOption[] | void> | void;
    clearable?: boolean;
    searchable?: boolean;
    placeholder?: string;
    ariaLabel?: string;
    hiddenLabel?: boolean;
    size?: 'small' | 'medium';
    fullWidth?: boolean;
    error?: boolean;
    disabled?: boolean;
    hideSelectedOptions?: boolean;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    id?: string;
    name?: string;
    required?: boolean;
    menuPlacement?: string;
    [key: string]: any;
}) => {
    const isMultiple = !!(multi || multiple);
    const [asyncOptions, setAsyncOptions] = React.useState<SelectOption[]>([]);
    const [loading, setLoading] = React.useState(false);
    const isReadOnly = !searchable;
    const availableOptions = loadOptions ? asyncOptions : options;
    const selectedValue = isMultiple
        ? resolveOptions(value, availableOptions)
        : resolveOption(value, availableOptions);

    const updateAsyncOptions = React.useCallback(
        (inputValue: string) => {
            if (!loadOptions) return;

            setLoading(true);
            const result = loadOptions(inputValue, (nextOptions) => {
                setAsyncOptions(nextOptions ?? []);
                setLoading(false);
            });

            if (result && typeof result.then === 'function') {
                result
                    .then((nextOptions) => {
                        if (nextOptions) {
                            setAsyncOptions(nextOptions);
                        }
                    })
                    .finally(() => setLoading(false));
            }
        },
        [loadOptions]
    );

    return (
        <Autocomplete
            id={id}
            className={className}
            disabled={disabled}
            disableClearable={!clearable}
            disableCloseOnSelect={isMultiple}
            filterOptions={loadOptions ? (x) => x : undefined}
            filterSelectedOptions={hideSelectedOptions}
            fullWidth={fullWidth}
            getOptionDisabled={(option) => !!option.disabled}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(option, selected) =>
                optionMatchesValue(option, selected)
            }
            loading={loading}
            multiple={isMultiple}
            onBlur={onBlur}
            onChange={(_, selected, reason, details) => {
                if (isMultiple) {
                    onChange?.(selected, {
                        removedValue: details?.option,
                        action: reason,
                    });
                    return;
                }

                onChange?.(selected ?? null, { action: reason });
            }}
            onInputChange={(_, inputValue, reason) => {
                if (reason === 'input' && loadOptions) {
                    updateAsyncOptions(inputValue);
                }
            }}
            openOnFocus
            options={availableOptions}
            renderInput={(params) => (
                <MuiTextField
                    {...params}
                    error={error}
                    helperText={errorText ?? helperText}
                    hiddenLabel={hiddenLabel}
                    label={hiddenLabel ? undefined : label}
                    name={name}
                    placeholder={placeholder}
                    required={required}
                    size={size}
                    slotProps={{
                        ...params.slotProps,

                        input: {
                            ...params.slotProps.input,
                            endAdornment: (
                                <>
                                    {loading && (
                                        <CircularProgress
                                            color="inherit"
                                            size={16}
                                        />
                                    )}
                                    {params.slotProps.input.endAdornment}
                                </>
                            ),
                        },

                        htmlInput: {
                            ...params.slotProps.htmlInput,
                            readOnly: isReadOnly,
                            'aria-label': hiddenLabel
                                ? (ariaLabel ??
                                  (typeof label === 'string'
                                      ? label
                                      : undefined))
                                : params.slotProps.htmlInput['aria-label'],
                        },
                    }}
                />
            )}
            renderOption={({ key, ...optionProps }, option) => (
                <Box
                    key={key}
                    component="li"
                    {...optionProps}
                    sx={{
                        pl: option.indentation
                            ? `${option.indentation + 1}rem`
                            : undefined,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                    }}
                >
                    <Typography variant="body2">
                        {optionLabel(option)}
                    </Typography>
                    {option.secondaryLabel && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                            }}
                        >
                            {option.secondaryLabel}
                        </Typography>
                    )}
                </Box>
            )}
            size={size}
            value={selectedValue as any}
            {...props}
            slotProps={{
                listbox: {
                    sx: {
                        '& .MuiAutocomplete-option': {
                            alignItems: 'flex-start',
                        },
                    },
                },
            }}
        />
    );
};

export const Checkbox = ({
    label,
    checked,
    onChange,
    ...props
}: {
    label?: ReactNode;
    checked?: boolean;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => void;
} & Record<string, any>) => {
    const { size = 'small', ...controlProps } = props;
    const control = (
        <MuiCheckbox
            checked={checked}
            onChange={onChange}
            size={size}
            {...controlProps}
        />
    );
    return label ? (
        <FormControlLabel control={control} label={label} />
    ) : (
        control
    );
};

export const Radio = ({
    label,
    checked,
    onChange,
    value,
    ...props
}: {
    label?: ReactNode;
    checked?: boolean;
    value?: any;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => void;
} & Record<string, any>) => {
    const { size = 'small', ...controlProps } = props;
    const control = (
        <MuiRadio
            checked={checked}
            onChange={onChange}
            value={value}
            size={size}
            {...controlProps}
        />
    );
    return label ? (
        <FormControlLabel control={control} label={label} />
    ) : (
        control
    );
};

export const Switch = (props: any) => <MuiSwitch {...props} />;

export const ErrorText = ({
    children,
    ...props
}: {
    children?: ReactNode;
    [key: string]: any;
}) =>
    children ? (
        <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block' }}
            {...props}
        >
            {children}
        </Typography>
    ) : null;

const toPickerValue = (value: any) => {
    if (!value) return null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed : null;
};

const toDateValue = (value: moment.Moment | null) =>
    value?.isValid() ? value.toDate() : null;

const omitLegacyPickerProps = ({
    disablePast,
    disableFuture,
    disableToolbar,
    format,
    invalidDateMessage,
    maxDate,
    maxDateMessage,
    minDate,
    minDateMessage,
    optional,
    variant,
    ...props
}: Record<string, any>) => props;

const pickerTextFieldSlotProps = ({
    error,
    errorText,
    fullWidth = true,
    helperText,
    hiddenLabel,
    id,
    inputProps,
    label,
    name,
    onBlur,
    placeholder,
    required,
    size = 'small',
}: any) => ({
    id,
    error,
    fullWidth,
    helperText: errorText || helperText,
    hiddenLabel,
    slotProps: {
        htmlInput: inputProps,
    },
    label,
    name,
    onBlur,
    placeholder,
    required,
    size,
    variant: 'filled' as const,
});

export const DatePicker = ({
    value,
    onChange,
    error,
    errorText,
    fullWidth,
    helperText,
    hiddenLabel,
    disablePast,
    disableFuture,
    id,
    inputProps,
    label,
    minDate,
    maxDate,
    name,
    onBlur,
    placeholder,
    required,
    size,
    slotProps,
    slots,
    ...props
}: any) => (
    <MuiDatePicker
        disablePast={disablePast}
        disableFuture={disableFuture}
        format="DD/MM/YYYY"
        label={label}
        maxDate={toPickerValue(maxDate) ?? undefined}
        minDate={toPickerValue(minDate) ?? undefined}
        onChange={(date: moment.Moment | null) => onChange?.(toDateValue(date))}
        slotProps={{
            ...slotProps,
            textField: {
                ...pickerTextFieldSlotProps({
                    error,
                    errorText,
                    fullWidth,
                    helperText,
                    hiddenLabel,
                    id,
                    inputProps,
                    label,
                    name,
                    onBlur,
                    placeholder,
                    required,
                    size,
                }),
                ...slotProps?.textField,
            },
        }}
        slots={{ openPickerIcon: CalendarBlank, ...slots }}
        value={toPickerValue(value)}
        {...omitLegacyPickerProps(props)}
    />
);

export const DateTimePicker = ({
    value,
    onChange,
    error,
    errorText,
    fullWidth,
    helperText,
    hiddenLabel,
    disablePast,
    disableFuture,
    id,
    inputProps,
    label,
    minDate,
    maxDate,
    name,
    onBlur,
    placeholder,
    required,
    size,
    slotProps,
    slots,
    ...props
}: any) => (
    <MuiDateTimePicker
        disablePast={disablePast}
        disableFuture={disableFuture}
        format="DD/MM/YYYY HH:mm"
        label={label}
        maxDateTime={toPickerValue(maxDate) ?? undefined}
        minDateTime={toPickerValue(minDate) ?? undefined}
        onChange={(date: moment.Moment | null) => onChange?.(toDateValue(date))}
        slotProps={{
            ...slotProps,
            textField: {
                ...pickerTextFieldSlotProps({
                    error,
                    errorText,
                    fullWidth,
                    helperText,
                    hiddenLabel,
                    id,
                    inputProps,
                    label,
                    name,
                    onBlur,
                    placeholder,
                    required,
                    size,
                }),
                ...slotProps?.textField,
            },
        }}
        slots={{ openPickerIcon: CalendarBlank, ...slots }}
        value={toPickerValue(value)}
        {...omitLegacyPickerProps(props)}
    />
);
